// Phase 9 R2 — Update item metadata via R1 RPC (whitelisted fields only).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface UpdateInventoryItemInput {
  id: string;
  part_number: string;
  name: string;
  brand?: string | null;
  description?: string | null;
  category?: string | null;
  supplier?: string | null;
  supplier_part_number?: string | null;
  unit_cost?: number;
  unit_sell_default?: number | null;
  reorder_point?: number;
  reorder_quantity?: number;
  bin_location?: string | null;
  notes?: string | null;
}

export function useUpdateInventoryItem() {
  const { activeShop } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateInventoryItemInput) => {
      const { data, error } = await supabase.rpc('update_inventory_item', {
        p_id: input.id,
        p_part_number: input.part_number,
        p_name: input.name,
        p_brand: input.brand ?? undefined,
        p_description: input.description ?? undefined,
        p_category: input.category ?? undefined,
        p_supplier: input.supplier ?? undefined,
        p_supplier_part_number: input.supplier_part_number ?? undefined,
        p_unit_cost: input.unit_cost,
        p_unit_sell_default: input.unit_sell_default ?? undefined,
        p_reorder_point: input.reorder_point,
        p_reorder_quantity: input.reorder_quantity,
        p_bin_location: input.bin_location ?? undefined,
        p_notes: input.notes ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-items', activeShop?.id] });
      toast.success('Item updated');
    },
    onError: (e: any) => toast.error(e?.message ?? 'Failed to update item'),
  });
}
